import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth";
import {
    LayoutDashboard, Users, FileText, Send, BarChart3, Settings,
    LogOut, ChevronLeft, ChevronRight, Plane, Compass
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true },
    { to: "/contacts", label: "Kontakte", icon: Users },
    { to: "/discovery", label: "Entdecken", icon: Compass },
    { to: "/templates", label: "Vorlagen", icon: FileText },
    { to: "/campaigns", label: "Kampagnen", icon: Send },
    { to: "/stats", label: "Statistik", icon: BarChart3 },
    { to: "/settings", label: "Einstellungen", icon: Settings },
];

export default function AppLayout({ children }) {
    const { user, logout } = useAuth();
    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const isActive = (to, exact) =>
        exact ? location.pathname === to : location.pathname === to || location.pathname.startsWith(to + "/");

    return (
        <div className="min-h-screen flex bg-[#f4f7f9]">
            {/* Sidebar */}
            <aside
                data-testid="app-sidebar"
                className={cn(
                    "fixed top-0 left-0 h-full bg-[#111d32] text-slate-300 flex flex-col z-40 transition-all duration-300 shadow-2xl",
                    collapsed ? "w-20" : "w-64"
                )}
            >
                <div className={cn("h-20 flex items-center border-b border-white/5", collapsed ? "justify-center" : "px-6")}>
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#007bff] to-[#1a2b48] flex items-center justify-center shadow-lg shadow-blue-500/20">
                            <Plane className="w-5 h-5 text-white -rotate-12" strokeWidth={2} />
                        </div>
                        {!collapsed && (
                            <div>
                                <div className="text-white font-semibold tracking-tight text-[15px] leading-tight">366Transfer</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-blue-400/80">Outreach</div>
                            </div>
                        )}
                    </div>
                </div>

                <nav className="flex-1 py-6 px-3 space-y-1">
                    {NAV.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.to, item.exact);
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                className={cn(
                                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium tracking-wide transition-all",
                                    active
                                        ? "bg-[#1a2b48] text-white shadow-sm border border-white/5"
                                        : "text-slate-400 hover:bg-[#1a2b48]/40 hover:text-white",
                                    collapsed && "justify-center px-0"
                                )}
                            >
                                <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.7} />
                                {!collapsed && <span>{item.label}</span>}
                            </Link>
                        );
                    })}
                </nav>

                <div className="border-t border-white/5 p-3">
                    {!collapsed && user && (
                        <div className="px-3 py-2 mb-2">
                            <div className="text-[11px] uppercase tracking-[0.2em] text-blue-400/70 mb-1">Eingeloggt als</div>
                            <div className="text-sm text-white font-medium truncate" data-testid="sidebar-user-email">{user.email}</div>
                        </div>
                    )}
                    <button
                        data-testid="logout-button"
                        onClick={logout}
                        className={cn(
                            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-rose-500/10 hover:text-rose-300 transition-colors",
                            collapsed && "justify-center px-0"
                        )}
                    >
                        <LogOut className="w-[18px] h-[18px]" strokeWidth={1.7} />
                        {!collapsed && <span>Abmelden</span>}
                    </button>
                    <button
                        data-testid="sidebar-collapse"
                        onClick={() => setCollapsed(!collapsed)}
                        className="w-full mt-2 flex items-center justify-center px-3 py-2 rounded-lg text-slate-500 hover:bg-white/5 hover:text-white transition-colors"
                    >
                        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <main
                className={cn("flex-1 transition-all duration-300", collapsed ? "ml-20" : "ml-64")}
                data-testid="main-content"
            >
                <div className="p-6 md:p-10 max-w-[1400px] mx-auto">{children}</div>
            </main>
        </div>
    );
}
